/**
 * Painting.ts — the core gameplay scene.
 *
 * Wires the full loop:
 *   pick color -> Buy (debits) -> tap cells -> drip coin pops -> color-complete
 *   stardust -> finish all colors -> picture-complete cinematic -> back to gallery.
 *
 * Layers (bottom -> top): evening bg, PaintCanvas, DripLayer, fxLayer (reward
 * bursts), HUD + FomoHud, PaletteDock, AutoplayPanel, overlay (cinematics).
 *
 * The client only REQUESTS reveals (tap_cell); the MockTransport/server is
 * authoritative and streams cell_reveal/color_complete/picture_complete frames.
 */
import { Container, Graphics } from 'pixi.js';
import { Scene } from '../app/Scene';
import type { AppContext, Viewport } from '../app/AppContext';
import theme, { hex } from '../render/theme';
import { vGradient, makeText } from '../ui/widgets';
import { Tweens } from '../util/tween';
import { easeOutCubic } from '../util/ease';
import { HUD } from '../ui/HUD';
import { FomoHud } from '../ui/FomoHud';
import { PaletteDock } from '../ui/PaletteDock';
import { InfoPanel } from '../ui/InfoPanel';
import { Button } from '../ui/widgets';
import { AutoplayPanel, type AutoplayConfig, type AutoplaySpeed } from '../ui/AutoplayPanel';
import { PaintCanvas } from './PaintCanvas';
import { DripLayer } from '../fx/DripLayer';
import { RewardChoreographer } from '../fx/RewardChoreographer';
import { Juice } from '../fx/Juice';
import { Audio } from '../util/audio';
import { makePictureRuntime, type ColorState, type PictureRuntime } from '../state/GameStore';
import { Progress } from '../state/progress';
import { getPictureById } from '../pictures/library';
import { randomHex } from '../util/rng';
import type {
  PictureInstanceResponse,
  PictureSummary,
  ServerWsMessage,
} from '../net/contract';
import { PictureSelect } from './PictureSelect';

const SPEED_PACE: Record<AutoplaySpeed, number> = { zen: 320, brisk: 150, turbo: 55 };

export class PaintingScene extends Scene {
  private bg = new Graphics();
  private world = new Container(); // shake target
  private canvasWrap = new Container();
  private dripLayer = new DripLayer();
  private fxLayer = new Container();
  private overlay = new Container();

  private hud!: HUD;
  private fomo = new FomoHud();
  private dock!: PaletteDock;
  private paintCanvas!: PaintCanvas;
  private fitBtn!: Button;
  private hintBtn!: Button;
  private autoplayPanel!: AutoplayPanel;
  private infoPanel: InfoPanel | null = null;
  private loadingPill: Container | null = null;
  private peekBtn!: Button;
  private realityCheckShownAt = 0;
  private reward!: RewardChoreographer;
  private juice = new Juice();

  private rt!: PictureRuntime;
  private summary: PictureSummary;
  private instance: PictureInstanceResponse;
  private canvasRect = { x: 0, y: 0, w: 0, h: 0 };
  private coach: Container | null = null;
  private coachBob = 0;
  private coachBaseY = 0;

  private unsubWs: (() => void) | null = null;
  private storeUnsubs: (() => void)[] = [];
  private vp!: Viewport;

  // tap pacing: queue of pending cell taps to space out reveals
  private activeBetId: string | null = null;
  private autoplay = false;
  private autoplayCfg: AutoplayConfig | null = null;

  constructor(ctx: AppContext, summary: PictureSummary, instance: PictureInstanceResponse) {
    super();
    this.init(ctx);
    this.summary = summary;
    this.instance = instance;
  }

  enter(): void {
    this.buildRuntime();
    this.buildLayers();
    this.wireTransport();
    this.layout(this.ctx.viewport);
    this.juice.attachShakeTarget(this.world);
    Audio.unlock();
  }

  // ---------- setup ----------
  private buildRuntime(): void {
    const def = getPictureById(this.summary.pictureId)!;
    const colors = new Map<number, ColorState>();
    const counts = new Map<number, number>();
    for (const c of this.instance.cells)
      counts.set(c.colorId, (counts.get(c.colorId) ?? 0) + 1);
    for (const pe of this.instance.palette) {
      colors.set(pe.colorId, {
        colorId: pe.colorId,
        number: pe.number,
        hex: pe.hex,
        cellCount: counts.get(pe.colorId) ?? pe.cellCount,
        bought: false,
        revealed: 0,
        completed: false,
        earned: 0,
      });
    }
    this.rt = makePictureRuntime({
      pictureInstanceId: this.instance.pictureInstanceId,
      pictureId: this.summary.pictureId,
      title: this.summary.title,
      sizeName: this.summary.sizeName,
      view: def.view,
      K: this.instance.palette.length,
      palette: this.instance.palette,
      cells: this.instance.cells,
      colors,
      totalStake: 0,
      totalEarned: 0,
      cellsTotal: this.instance.cells.length,
      cellsRevealed: 0,
      projectedTeaser: 0,
    });
    this.ctx.store.loadPicture(this.rt);
  }

  private buildLayers(): void {
    this.addChild(this.bg, this.world, this.overlay);
    this.world.addChild(this.canvasWrap, this.dripLayer, this.fxLayer);

    const def = getPictureById(this.summary.pictureId)!;
    this.paintCanvas = new PaintCanvas(
      this.instance.cells,
      this.instance.palette,
      def.view,
      {
        onTapCell: (colorId, cellIndex, sx, sy) =>
          this.requestReveal(colorId, cellIndex, sx, sy),
        onMissTap: () => {
          Audio.unlock();
          Audio.dud();
          Juice.haptic(0.12);
        },
      },
      def.image,
      def.ink,
    );
    this.canvasWrap.addChild(this.paintCanvas);

    // tiny "loading the picture" pill that fades out once the async number-build is done — kills
    // the perceived entry hitch on big pictures (108+ regions allocate a lot of Pixi Texts).
    this.loadingPill = new Container();
    const loadBg = new Graphics();
    const loadTxt = makeText('Loading the picture…', { size: 13, weight: '800', color: theme.color.ink });
    const w = loadTxt.width + 28;
    loadBg.roundRect(-w / 2, -14, w, 28, 14).fill({ color: theme.color.white, alpha: 0.94 });
    loadBg.roundRect(-w / 2, -14, w, 28, 14).stroke({ width: 1, color: theme.color.hint, alpha: 0.5 });
    loadTxt.anchor.set(0.5); loadTxt.position.set(0, 0);
    this.loadingPill.addChild(loadBg, loadTxt);
    this.overlay.addChild(this.loadingPill);
    this.paintCanvas.onNumbersReady = () => {
      Tweens.to({
        from: 1, to: 0, duration: theme.motion.base, ease: easeOutCubic,
        onUpdate: (v) => { if (this.loadingPill) this.loadingPill.alpha = v; },
        onComplete: () => { this.loadingPill?.destroy({ children: true }); this.loadingPill = null; },
      });
    };

    // floating canvas controls: zoom-to-fit + hint + peek (paint-by-numbers affordances)
    this.fitBtn = new Button({
      width: 46, height: 46, label: '⛶', radius: 23,
      fill: theme.color.white, textColor: theme.color.ink, fontSize: 22,
      onTap: () => this.paintCanvas.zoomToFit(),
    });
    this.hintBtn = new Button({
      width: 46, height: 46, label: '💡', radius: 23,
      fill: theme.color.white, textColor: theme.color.ink, fontSize: 20,
      onTap: () => this.useHint(),
    });
    // press-and-hold "peek": briefly fades the unpainted lids so the player can glance at the
    // finished target picture. Releasing returns the lids to full opacity. Tap = same as
    // press-and-release (a quick reveal flash). One of the small parity affordances good PBN
    // games offer — kills the "am I painting the right thing?" anxiety without giving away
    // the satisfaction of finishing.
    this.peekBtn = new Button({
      width: 46, height: 46, label: '👁', radius: 23,
      fill: theme.color.white, textColor: theme.color.ink, fontSize: 18,
      onTap: () => this.paintCanvas.flashPeek(),
    });
    this.peekBtn.on('pointerdown', () => this.paintCanvas.setPeek(true));
    this.peekBtn.on('pointerup', () => this.paintCanvas.setPeek(false));
    this.peekBtn.on('pointerupoutside', () => this.paintCanvas.setPeek(false));

    // HUD
    this.hud = new HUD({
      title: this.rt.title,
      balance: this.ctx.store.balance,
      onBack: () => this.backToGallery(),
      onInfo: () => this.toggleInfoPanel(),
      funMode: this.ctx.transport.kind === 'mock',
    });

    // dock
    const colorStates = Array.from(this.rt.colors.values());
    const betLevels = this.ctx.store.mathConfig?.betLevels ?? [0.1, 0.5, 1, 5];
    const stakeIdx = betLevels.indexOf(this.ctx.store.selectedStake);
    this.dock = new PaletteDock(colorStates, betLevels, stakeIdx >= 0 ? stakeIdx : 3, {
      onSelectColor: (id) => this.selectColor(id),
      onBuy: (id, stake) => this.buyColor(id, stake),
      onStakeChange: (stake) => this.ctx.store.setStake(stake),
      onAutoplay: () => this.toggleAutoplay(),
    });

    // autoplay panel
    this.autoplayPanel = new AutoplayPanel(
      betLevels,
      this.ctx.store.selectedStake,
      this.rt.K,
      (cfg) => this.startAutoplay(cfg),
    );

    // reward choreographer
    this.reward = new RewardChoreographer({
      fxLayer: this.fxLayer,
      overlay: this.overlay,
      viewportW: this.ctx.viewport.width,
      viewportH: this.ctx.viewport.height,
      juice: this.juice,
    });

    this.addChild(this.fomo, this.hud, this.fitBtn, this.hintBtn, this.peekBtn, this.dock, this.autoplayPanel);

    this.maybeShowCoach();

    // store subscriptions
    this.storeUnsubs.push(
      this.ctx.store.events.on('balance', (b) => this.hud.setBalance(b)),
      this.ctx.store.events.on('sessionPL', (p) => this.hud.setNetPL(p.net)),
      this.ctx.store.events.on('progress', (p) => {
        const frac = p.total > 0 ? p.revealed / p.total : 0;
        this.hud.setProgress(frac);
        // remember how far the player got, so the gallery can offer "continue painting"
        Progress.update(this.summary.pictureId, { progress01: frac, lastOpened: Date.now() });
      }),
      this.ctx.store.events.on('colorChanged', (c) => this.dock.refreshColor(c)),
      this.ctx.store.events.on('fomo', ({ colorsRemaining, teaser }) => {
        const { total } = this.ctx.store.completion();
        this.fomo.update(total - colorsRemaining, total, teaser);
      }),
    );

    // initial fomo
    const comp = this.ctx.store.completion();
    this.fomo.update(comp.done, comp.total, this.rt.projectedTeaser);
  }

  private wireTransport(): void {
    void this.ctx.transport.connect();
    this.unsubWs = this.ctx.transport.onMessage((m) => this.onServerMessage(m));
  }

  // ---------- interactions ----------
  private selectColor(colorId: number): void {
    const cs = this.rt.colors.get(colorId);
    if (!cs) return;
    if (cs.bought && !cs.completed) {
      this.activeBetId = cs.betId ?? null;
      this.paintCanvas.setActiveColor(colorId);
    } else {
      // not paintable yet (unbought preview-select, or already completed)
      this.paintCanvas.setActiveColor(null);
    }
  }

  private async buyColor(colorId: number, stake: number): Promise<void> {
    const cs = this.rt.colors.get(colorId);
    if (!cs || cs.bought) return;
    if (this.ctx.store.balance < stake) {
      this.flashInsufficient();
      return;
    }
    Audio.unlock();
    Audio.tap();
    try {
      const resp = await this.ctx.transport.buyColor({
        colorId,
        stake,
        // 32 hex chars = 128 bits — collision-safe across an entire session
        idempotencyKey: 'buy-' + randomHex(32),
      });
      this.ctx.store.markBought(colorId, resp.betId, stake);
      this.ctx.store.setBalance(resp.balanceAfterDebit);
      this.dismissCoach();
      this.activeBetId = resp.betId;
      // if the active colour's next region is off-screen at current zoom, fly there
      setTimeout(() => this.paintCanvas.flyToNextActiveIfOffscreen(colorId), 350);
      this.paintCanvas.setActiveColor(colorId);
      this.dock.refreshColor(this.rt.colors.get(colorId)!);
      Juice.squash(this.dock, 0.05, theme.motion.base);
      Audio.resetChime();
      if (this.autoplay) this.autoTapColor(colorId, resp.betId);
    } catch (e) {
      this.flashInsufficient();
      void e;
    }
  }

  // ---------- transparency: info / paytable / provably-fair ----------
  private toggleInfoPanel(): void {
    if (this.infoPanel) {
      this.infoPanel.destroy({ children: true });
      this.infoPanel = null;
      return;
    }
    if (!this.ctx.store.mathConfig) return;
    const panel = new InfoPanel({
      onClose: () => this.toggleInfoPanel(),
      // limits panel deferred — opens BeGambleAware help link as a stand-in for now
      onOpenLimits: () => {
        if (typeof window !== 'undefined') window.open('https://www.begambleaware.org/', '_blank');
      },
    });
    const pf = {
      serverSeedHash: this.ctx.store.serverSeedHash,
      clientSeed: this.ctx.store.clientSeed,
      nonce: this.ctx.store.nonce,
      mathVersion: this.ctx.store.mathVersion,
    };
    panel.render(this.ctx.store.mathConfig, pf);
    panel.layout(this.ctx.viewport);
    this.overlay.addChild(panel);
    this.infoPanel = panel;
  }

  // ---------- first-run onboarding ----------
  private maybeShowCoach(): void {
    let seen = false;
    try { seen = typeof localStorage !== 'undefined' && localStorage.getItem('colordrop.onboarded') === '1'; } catch { seen = false; }
    if (seen) return;
    const c = new Container();
    const label = makeText('Pick a colour → Buy → swipe to paint', {
      size: 12, weight: '800', color: theme.color.white, align: 'center',
    });
    label.anchor.set(0.5);
    const w = label.width + 28;
    const bg = new Graphics();
    bg.roundRect(-w / 2, -16, w, 32, 16).fill({ color: hex(theme.palette.purpleDeep), alpha: 0.94 });
    bg.roundRect(-w / 2, -16, w, 32, 16).stroke({ width: 1, color: theme.color.white, alpha: 0.3 });
    // little downward pointer toward the dock
    bg.poly([-8, 16, 8, 16, 0, 26]).fill({ color: hex(theme.palette.purpleDeep), alpha: 0.94 });
    c.addChild(bg, label);
    this.coach = c;
    this.addChild(c);
    // auto-dismiss after a few seconds even if the player hasn't bought yet — a hint, not a wall
    Tweens.delay(7000, () => { if (this.coach === c) this.dismissCoach(); });
  }

  private dismissCoach(): void {
    try { if (typeof localStorage !== 'undefined') localStorage.setItem('colordrop.onboarded', '1'); } catch { /* ignore */ }
    const c = this.coach;
    this.coach = null;
    if (!c) return;
    Tweens.to({
      from: 1, to: 0, duration: theme.motion.base, ease: easeOutCubic,
      onUpdate: (v) => { if (!c.destroyed) c.alpha = v; },
      onComplete: () => { if (!c.destroyed) c.destroy({ children: true }); },
    });
  }

  /** Hint: fly the camera to the next region the player should paint and ring it. */
  private useHint(): void {
    Audio.unlock();
    Audio.tap();
    // 1) the active colour's next unpainted region; 2) any bought-not-completed colour's next.
    const target = this.nextHintTarget();
    if (target) {
      this.paintCanvas.focusRegion(target.colorId, target.indexInColor);
      return;
    }
    // nothing bought yet -> nudge the player to buy a colour
    Juice.squash(this.dock, 0.06, theme.motion.base);
  }

  private nextHintTarget(): { colorId: number; indexInColor: number } | null {
    const tryColor = (colorId: number): { colorId: number; indexInColor: number } | null => {
      const idx = this.paintCanvas.nextUnfilledIndex(colorId);
      return idx == null ? null : { colorId, indexInColor: idx };
    };
    if (this.activeBetId != null) {
      const active = this.colorForBet(this.activeBetId);
      if (active != null) {
        const t = tryColor(active);
        if (t) return t;
      }
    }
    let found: { colorId: number; indexInColor: number } | null = null;
    this.rt.colors.forEach((c) => {
      if (found || !c.bought || c.completed) return;
      found = tryColor(c.colorId);
    });
    return found;
  }

  private requestReveal(colorId: number, cellIndex: number, _sx: number, _sy: number): void {
    if (this.activeBetId == null) return;
    const cs = this.rt.colors.get(colorId);
    if (!cs || !cs.bought || cs.completed) return;
    Audio.unlock();
    Audio.tap();
    this.ctx.transport.send({ t: 'tap_cell', betId: this.activeBetId, cellIndex });
  }

  // auto-tap all cells of a color, paced by autoplay speed
  private autoTapColor(colorId: number, betId: string): void {
    const cs = this.rt.colors.get(colorId);
    if (!cs) return;
    const pace = SPEED_PACE[this.autoplayCfg?.speed ?? 'brisk'];
    let i = 0;
    const total = cs.cellCount;
    const step = () => {
      if (!this.autoplay) return;
      // pause the run while the tab is hidden; resume when it comes back
      if (typeof document !== 'undefined' && document.hidden) {
        setTimeout(step, 400);
        return;
      }
      if (i >= total) {
        // after color done, move to next unbought color
        this.scheduleNextAutoplayColor();
        return;
      }
      this.ctx.transport.send({ t: 'tap_cell', betId, cellIndex: i });
      i++;
      setTimeout(step, pace);
    };
    setTimeout(step, pace);
  }

  private scheduleNextAutoplayColor(): void {
    if (!this.autoplay || !this.autoplayCfg) return;
    // find next unbought color
    let next: ColorState | null = null;
    this.rt.colors.forEach((c) => {
      if (!next && !c.bought) next = c;
    });
    if (!next) return; // picture will resolve on its own
    setTimeout(() => {
      if (!this.autoplay) return;
      const n = next as ColorState;
      this.dock.selectColor(n.colorId);
      this.buyColor(n.colorId, this.autoplayCfg!.stake);
    }, 300);
  }

  // ---------- server stream ----------
  private onServerMessage(m: ServerWsMessage): void {
    switch (m.t) {
      case 'cell_reveal':
        this.onCellReveal(m);
        break;
      case 'color_complete':
        this.onColorComplete(m);
        break;
      case 'picture_complete':
        this.onPictureComplete(m);
        break;
      case 'fomo':
        this.ctx.store.setFomo(m.colorsRemaining, m.projectedBonusTeaser);
        break;
      case 'balance':
        this.ctx.store.setBalance(m.amount);
        break;
      case 'autoplay_round':
        // (HUD ticker could show; kept minimal)
        break;
      case 'autoplay_done':
        this.endAutoplay();
        break;
      case 'error':
        this.flashInsufficient();
        break;
    }
  }

  private onCellReveal(m: Extract<ServerWsMessage, { t: 'cell_reveal' }>): void {
    // figure out which color via active bet mapping
    const colorId = this.colorForBet(m.betId);
    if (colorId == null) return;
    const cs = this.rt.colors.get(colorId);
    if (!cs) return;
    const idxInColor = m.cellIndex;
    // For manual taps the cell was already painted optimistically; this is a no-op then. For
    // autoplay-driven reveals it performs the fill. The sparkle is always server-gated.
    this.paintCanvas.fillCell(colorId, idxInColor);
    if (m.sparkle) this.paintCanvas.flashSparkle(colorId, idxInColor);
    this.ctx.store.recordCellReveal(colorId, m.subAmount);

    const pos = this.paintCanvas.cellScreenPos(colorId, idxInColor);
    // pos is in scene space already (relative to this scene); DripLayer is inside world
    const local = this.dripLayer.toLocal({ x: pos.x, y: pos.y }, this);
    // Feed the rolling combo tracker on EVERY reveal (paid or zero) — what matters for the feel
    // of a rapid sweep is the density of revealed cells, not their per-cell payout.
    Audio.noteReveal();
    if (m.subAmount > 0) {
      const mag = Math.min(1, m.subMult / 3);
      // the coin flies to the balance pill and the wallet "catches" it on arrival
      const bal = this.hud.balanceScreenPos();
      const balLocal = this.dripLayer.toLocal({ x: bal.x, y: bal.y }, this);
      this.dripLayer.coinPop(local.x, local.y, m.subAmount, mag, m.sparkle ?? false, {
        target: balLocal,
        onArrive: () => this.hud.catchCoin(),
      });
      if (m.sparkle) Audio.sparkle();
      else Audio.coin(mag);
      Audio.chime();
      if (mag > 0.5) this.juice.shake(0.06 + mag * 0.08);
    } else {
      this.dripLayer.emptyRipple(local.x, local.y);
    }
  }

  private onColorComplete(m: Extract<ServerWsMessage, { t: 'color_complete' }>): void {
    const colorId = this.colorForBet(m.betId);
    if (colorId == null) return;
    this.ctx.store.recordColorComplete(colorId, m.bonusAmount);
    const cs = this.rt.colors.get(colorId)!;
    this.paintCanvas.celebrateColor(colorId);
    const pos = this.paintCanvas.cellScreenPos(colorId, cs.cellCount - 1);
    const local = this.fxLayer.toLocal({ x: pos.x, y: pos.y }, this);
    this.reward.colorComplete(local.x, local.y, m.bonusAmount, m.bonusMult, hex(cs.hex));

    // clear active color
    this.activeBetId = null;
    this.paintCanvas.setActiveColor(null);
    this.dock.refreshColor(cs);

    // update fomo
    const comp = this.ctx.store.completion();
    this.fomo.update(comp.done, comp.total, this.rt.projectedTeaser);
  }

  private onPictureComplete(m: Extract<ServerWsMessage, { t: 'picture_complete' }>): void {
    this.ctx.store.recordPictureComplete(m.bonusAmount, m.bonusMult, m.tier);
    Progress.update(this.summary.pictureId, { progress01: 1, completed: true, lastOpened: Date.now() });
    const accent = this.instance.palette.slice(0, 6).map((p) => hex(p.hex));
    // stop autoplay before cinematic
    this.endAutoplay();
    this.reward.pictureComplete(
      m.bonusAmount, m.bonusMult, m.tier, accent,
      {
        canvasRect: this.canvasRect,
        onRevealArt: () => this.paintCanvas.fadeInk(0, 700),
        totalStake: this.rt.totalStake,
      },
      () => this.backToGallery(),
    );
  }

  private colorForBet(betId: string): number | null {
    let found: number | null = null;
    this.rt.colors.forEach((c) => {
      if (c.betId === betId) found = c.colorId;
    });
    return found;
  }

  // ---------- autoplay ----------
  private toggleAutoplay(): void {
    if (this.autoplay) {
      void this.ctx.transport.stopAutoplay({ autoplayRunId: this.ctx.store.autoplayRunId ?? '' });
      this.endAutoplay();
    } else {
      this.autoplayPanel.show(this.vp);
    }
  }

  private async startAutoplay(cfg: AutoplayConfig): Promise<void> {
    this.autoplay = true;
    this.autoplayCfg = cfg;
    this.dock.setAutoplayActive(true);
    this.ctx.store.setStake(cfg.stake);
    this.dock.setStakeIndexForValue(cfg.stake);

    // build stake template across all not-yet-bought colors
    const template = Array.from(this.rt.colors.values())
      .filter((c) => !c.bought)
      .map((c) => ({ colorId: c.colorId, stake: cfg.stake }));

    const resp = await this.ctx.transport.startAutoplay({
      rounds: cfg.rounds,
      stakeTemplate: template,
      stop: cfg.stop,
    });
    this.ctx.store.setAutoplay(true, resp.autoplayRunId);

    // kick the client-side visual loop: buy first unbought color
    let first: ColorState | null = null;
    this.rt.colors.forEach((c) => {
      if (!first && !c.bought) first = c;
    });
    if (first) {
      const f = first as ColorState;
      this.dock.selectColor(f.colorId);
      // small delay so the user sees the start
      setTimeout(() => this.buyColor(f.colorId, cfg.stake), 250);
    }
  }

  private endAutoplay(): void {
    if (!this.autoplay && !this.ctx.store.autoplayRunning) return;
    this.autoplay = false;
    this.autoplayCfg = null;
    this.dock.setAutoplayActive(false);
    this.ctx.store.setAutoplay(false, null);
  }

  // ---------- misc ----------
  private flashInsufficient(): void {
    Juice.haptic(0.2);
    const g = new Graphics();
    g.rect(0, 0, this.vp.width, this.vp.height).fill({ color: theme.color.coral, alpha: 0 });
    this.overlay.addChild(g);
    let a = 0;
    const id = setInterval(() => {
      a += 0.12;
      g.clear();
      g.rect(0, 0, this.vp.width, this.vp.height).fill({
        color: theme.color.coral,
        alpha: Math.sin(a) * 0.12,
      });
      if (a > Math.PI) {
        clearInterval(id);
        g.destroy();
      }
    }, 16);
  }

  private backToGallery(): void {
    this.endAutoplay();
    this.ctx.scenes.go(() => new PictureSelect());
  }

  override update(dtMs: number): void {
    this.juice.update();
    this.fomo.tick(dtMs);
    this.paintCanvas.tick(dtMs);
    if (this.coach) {
      this.coachBob += dtMs * 0.005;
      this.coach.position.y = this.coachBaseY + Math.sin(this.coachBob) * 4;
    }
    // UKGC reality-check: at most once per 15 min, show the info panel containing net P/L +
    // session time so the player has to actively acknowledge how long they've been playing.
    const now = Date.now();
    if (now - (this.realityCheckShownAt || this.ctx.store.sessionStartMs) > 15 * 60 * 1000) {
      this.realityCheckShownAt = now;
      if (!this.infoPanel) this.toggleInfoPanel();
    }
  }

  override exit(): void {
    this.unsubWs?.();
    this.storeUnsubs.forEach((u) => u());
    this.storeUnsubs = [];
  }

  layout(v: Viewport): void {
    this.vp = v;
    this.bg.clear();
    this.bg.rect(0, 0, v.width, v.height).fill(vGradient(theme.gradient.galleryBg, v.height));
    // studio vignette: a soft inset dark frame so the eye settles on the canvas in the middle —
    // concentric translucent rounded rects darkening toward the screen edges (cheap radial-ish).
    const vc = hex(theme.palette.bgDark1);
    for (let i = 0; i < 5; i++) {
      const inset = -40 + i * 22;
      this.bg.roundRect(inset, inset, v.width - inset * 2, v.height - inset * 2, 40)
        .stroke({ width: 26, color: vc, alpha: 0.012 });
    }

    // HUD top
    this.hud.layout(v);
    const hudBottom = v.insetTop + 10 + this.hud.panelHeight;
    // fomo just under hud
    this.fomo.layout(v, hudBottom + 8);
    const fomoBottom = hudBottom + 8 + 50;

    // dock bottom
    this.dock.layout(v);
    const dockTop = v.height - (188 + v.insetBottom) - 8;

    // canvas occupies the middle. Reserve a TOOLBAR STRIP below it for ⛶/💡/👁 so they never
    // sit on top of the artwork (audit HIGH: floating pucks covered small regions + the invite
    // rings in the lower-right corner).
    const toolStrip = 52;
    const cTop = fomoBottom + 12;
    const cH = dockTop - cTop - 12 - toolStrip;
    const cW = v.width - 24;
    this.canvasRect = { x: 12, y: cTop, w: cW, h: cH };
    this.canvasWrap.position.set(12, cTop);
    // canvas board area card backdrop
    this.drawCanvasFrame(cW, cH);
    this.paintCanvas.position.set(0, 0);
    this.paintCanvas.fit(cW, cH);

    // tools in a horizontal toolbar in the reserved strip, right-aligned, BELOW the canvas:
    //   👁 peek (press & hold) · 💡 hint · ⛶ fit  (fit closest to the right thumb)
    const by = cTop + cH + (toolStrip - 46) / 2;
    const rx = 12 + cW - 46;
    this.fitBtn.position.set(rx, by);
    this.hintBtn.position.set(rx - 56, by);
    this.peekBtn.position.set(rx - 112, by);

    this.autoplayPanel.layout(v);

    if (this.coach) {
      this.coachBaseY = dockTop - 26;
      this.coach.position.set(v.width / 2, this.coachBaseY);
    }
    if (this.loadingPill) this.loadingPill.position.set(v.width / 2, fomoBottom + 28);
    if (this.infoPanel) this.infoPanel.layout(v);

    if (this.reward) {
      // keep reward dimensions current
      (this.reward as unknown as { ctx: { viewportW: number; viewportH: number } }).ctx.viewportW = v.width;
      (this.reward as unknown as { ctx: { viewportW: number; viewportH: number } }).ctx.viewportH = v.height;
    }
  }

  private canvasFrame = new Graphics();
  private drawCanvasFrame(w: number, h: number): void {
    if (!this.canvasFrame.parent) this.canvasWrap.addChildAt(this.canvasFrame, 0);
    const g = this.canvasFrame;
    g.clear();
    const shadowColor = theme.shadow.soft.color;
    // soft drop shadow (stacked translucent rounded rects) so the picture lifts off the background
    for (let i = 3; i >= 1; i--) {
      g.roundRect(-6, -6 + i * 4, w + 12, h + 12, theme.radius.card)
        .fill({ color: shadowColor, alpha: 0.05 });
    }
    // white matte frame around the artwork (a framed-print feel)
    g.roundRect(-8, -8, w + 16, h + 16, theme.radius.card).fill({ color: theme.color.white, alpha: 0.92 });
    g.roundRect(-8, -8, w + 16, h + 16, theme.radius.card).stroke({ width: 1, color: shadowColor, alpha: 0.12 });
    // subtle inner bevel line where the matte meets the art
    g.roundRect(-1, -1, w + 2, h + 2, theme.radius.cardSm).stroke({ width: 1, color: shadowColor, alpha: 0.1 });
  }
}
